// User types
export interface User {
  id: string;
  email: string;
  name: string;
  picture: string;
  credits: {
    total: number; // Total credits remaining (lifetime, not daily)
  };
}

// Journey types
export enum Stage {
  PersonalInfo = 0,
  Education = 1,
  EmploymentHistory = 2,
  Projects = 3,
  SkillsCertifications = 4,
  Review = 5,
  TemplateSelection = 6,
  Customization = 7,
  Generation = 8,
}

export type JourneyStatus = 'in_progress' | 'completed' | 'abandoned';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  stage: Stage;
}

export interface PersonalInfo {
  name: string;
  email: string;
  phone: string;
  location?: string;
  linkedin?: string;
  github?: string;
  portfolio?: string;
  currentDesignation?: string;
  targetPosition?: string;
}

export interface Employment {
  id?: string;
  company: string;
  position: string;
  location?: string;
  startDate: string;
  endDate?: string;
  isCurrent?: boolean;
  current?: boolean;
  description?: string;
  achievements?: string[];
}

export interface Project {
  name: string;
  description: string;
  technologies: string[];
  startDate?: string;
  endDate?: string;
  url?: string;
  highlights: string[];
  impact?: string;
}

export interface Education {
  id?: string;
  institution: string;
  degree: string;
  field?: string;
  location?: string;
  startDate: string;
  endDate?: string;
  gpa?: string;
  honors?: string[] | string;
}

export interface Certification {
  id?: string;
  name: string;
  issuer: string;
  issueDate?: string;
  dateObtained?: string;
  expiryDate?: string;
  credentialId?: string;
  url?: string;
}

export interface Award {
  title: string;
  issuer: string;
  date: string;
  description?: string;
}

export interface Publication {
  title: string;
  publisher: string;
  date: string;
  url?: string;
  description?: string;
}

export interface Volunteer {
  organization: string;
  role: string;
  startDate: string;
  endDate?: string;
  description: string;
}

export interface ResumeData {
  personal: PersonalInfo;
  employment: Employment[];
  projects: Project[];
  education: Education[];
  skills: string[];
  certifications: Certification[];
  awards: Award[];
  publications: Publication[];
  volunteer: Volunteer[];
  summary: string;
}

export interface Customization {
  sectionOrder: string[];
  hiddenSections: string[];
  colors: { [key: string]: string };
  fonts: {
    headerFont: string;
    bodyFont: string;
    fontSize: number;
  };
  spacing: {
    lineHeight: number;
    sectionSpacing: number;
    margins: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
  };
}

export interface ResumeJourney {
  id: string;
  userId: string;
  status: JourneyStatus;
  currentStage: Stage;
  conversationHistory: Message[];
  collectedData: ResumeData;
  selectedTemplate?: string;
  customization?: Customization;
  createdAt: string;
  updatedAt: string;
}

export interface StageQuestions {
  stage: Stage;
  description: string;
  questions: string[];
}

// Resume types
export interface Resume {
  id: string;
  userId: string;
  journeyId: string;
  templateId: string;
  data: ResumeData;
  customization: Customization;
  pdfUrl?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  rationale: string;
  previewUrl: string;
  isAtsFriendly: boolean;
  defaultCustomization: Customization;
}

// ATS Checker types
export interface ATSRecommendation {
  category: string;
  priority: string;
  suggestion: string;
}

export interface KeywordAnalysis {
  present: string[];
  missing: string[];
  score: number;
}

export interface ATSAnalysis {
  overallScore: number;
  category: string;
  strengths: string[];
  weaknesses: string[];
  recommendations: ATSRecommendation[];
  keywordAnalysis: KeywordAnalysis;
  formattingScore: number;
  contentScore: number;
  impactScore: number;
}

// API types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

